import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Settings } from "lucide-react";
import { PushToggle } from "@/components/PushToggle";
import { useAuth } from "@/lib/auth";

export function SettingsDialog({ onSaved }) {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && isAdmin) api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, [open, isAdmin]);

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        email: s.email || "",
        reminders_enabled: s.reminders_enabled,
        email_enabled: s.email_enabled,
        in_app_enabled: s.in_app_enabled,
        high_impact_only: s.high_impact_only,
        lead_hours: Number(s.lead_hours),
      });
      toast.success("Reminder settings saved");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="settings-button"
          className="w-9 h-9 rounded-lg grid place-items-center bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
          <Settings size={15} className="text-[#9CA3AF]" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-white/10 text-[#F9FAFB]">
        <DialogHeader>
          <DialogTitle className="font-display">Alert Settings</DialogTitle>
          <DialogDescription className="text-xs text-[#9CA3AF]">
            Release reminders, scalp signals, pre-news and post-news verdicts — in-app, push or email.
          </DialogDescription>
        </DialogHeader>

        {!isAdmin && (
          <div className="space-y-4" data-testid="settings-member-view">
            <PushToggle />
            <p className="text-[11px] text-[#6B7280]">Release reminders, pre-news and post-news alerts appear in the bell automatically.</p>
          </div>
        )}

        {isAdmin && s && (
          <div className="space-y-4">
            <Row label="Enable reminders" desc="Master switch for all alerts">
              <Switch data-testid="toggle-reminders" checked={s.reminders_enabled}
                onCheckedChange={(v) => set("reminders_enabled", v)} />
            </Row>
            <Row label="In-app notifications" desc="Bell alerts inside the app">
              <Switch data-testid="toggle-inapp" checked={s.in_app_enabled}
                onCheckedChange={(v) => set("in_app_enabled", v)} />
            </Row>
            <Row label="High-impact only" desc="Only NFP/CPI/PCE/GDP/FOMC etc.">
              <Switch data-testid="toggle-highimpact" checked={s.high_impact_only}
                onCheckedChange={(v) => set("high_impact_only", v)} />
            </Row>

            <PushToggle />

            <div className="space-y-2 pt-2 border-t border-white/10">
              <Row label="Email alerts" desc="Also send reminders to your inbox">
                <Switch data-testid="toggle-email" checked={s.email_enabled}
                  onCheckedChange={(v) => set("email_enabled", v)} />
              </Row>
              {s.email_enabled && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#9CA3AF]">Email address</Label>
                  <Input data-testid="settings-email-input" type="email" value={s.email || ""}
                    onChange={(e) => set("email", e.target.value)} placeholder="you@email.com"
                    className="bg-[#0A0D14] border-white/10 font-mono-num" />
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#9CA3AF]">Lead time before release</Label>
              <Select value={String(s.lead_hours)} onValueChange={(v) => set("lead_hours", v)}>
                <SelectTrigger data-testid="lead-time-select" className="bg-[#0A0D14] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-white/10 text-[#F9FAFB]">
                  <SelectItem value="1">1 hour before</SelectItem>
                  <SelectItem value="6">6 hours before</SelectItem>
                  <SelectItem value="24">24 hours before</SelectItem>
                  <SelectItem value="48">2 days before</SelectItem>
                  <SelectItem value="72">3 days before</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {isAdmin && (
          <DialogFooter>
            <Button data-testid="settings-save-button" onClick={save} disabled={saving || !s}
              className="bg-[#F59E0B] hover:bg-[#d98c08] text-black font-semibold">
              {saving ? "Saving…" : "Save Settings"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, desc, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-[11px] text-[#6B7280]">{desc}</div>
      </div>
      {children}
    </div>
  );
}
